const TYPE_ERROR_TYPE = 'TypeError';
const FAILED_TO_FETCH_GTM_MESSAGE =
  'Failed to fetch (www.googletagmanager.com)';
const GOOGLE_OTHER = 'GoogleOther';
const GTM_TD_PATH_SNIPPET = 'www.googletagmanager.com/td';
const GTM_LATENCY_KEY = 'gtm_latency';

type FrameLike = {
  filename?: string;
  function?: string;
  abs_path?: string;
};

type ExceptionLike = {
  type?: string;
  value?: string;
  stacktrace?: {
    frames?: FrameLike[];
  };
};

type BreadcrumbLike = {
  message?: string;
  url?: string;
  data?: Record<string, unknown>;
};

type RequestLike = {
  headers?: Record<string, unknown> | Array<[string, unknown]>;
  query_string?: unknown;
};

type GtmDiagnosticsEventLike = {
  exception?: {
    values?: ExceptionLike[];
  };
  request?: RequestLike;
  breadcrumbs?: BreadcrumbLike[];
  contexts?: Record<string, unknown>;
};

const LIPS_SOURCE_PATTERNS = ['/_next/', '/src/', 'lips.org.uk'];

// Sentry SDK / Application Insights instrumentation frames must NOT count as
// LIPS business code. A crawler beacon failure whose only app-looking frames
// are telemetry plumbing is still crawler noise.
const INSTRUMENTATION_PATTERNS = [
  '@sentry',
  'sentry',
  'applicationinsights',
  'appinsights',
];

function framePath(frame: FrameLike): string {
  return `${frame.abs_path ?? ''} ${frame.filename ?? ''}`;
}

function isLipsSourcePath(frame: FrameLike): boolean {
  return LIPS_SOURCE_PATTERNS.some((pattern) =>
    framePath(frame).includes(pattern),
  );
}

function isInstrumentationFrame(frame: FrameLike): boolean {
  const path = framePath(frame);
  const fn = (frame.function ?? '').toLowerCase();
  return INSTRUMENTATION_PATTERNS.some(
    (pattern) => path.includes(pattern) || fn.includes(pattern),
  );
}

function isLipsBusinessFrame(frame: FrameLike): boolean {
  return isLipsSourcePath(frame) && !isInstrumentationFrame(frame);
}

function hasExactGtmTypeErrorValue(
  candidate: GtmDiagnosticsEventLike,
): boolean {
  return (
    candidate.exception?.values?.some(
      (exception) =>
        exception.type === TYPE_ERROR_TYPE &&
        exception.value === FAILED_TO_FETCH_GTM_MESSAGE,
    ) ?? false
  );
}

function hasGoogleOtherInHeaders(request: RequestLike | undefined): boolean {
  const headers = request?.headers;
  if (!headers) return false;

  if (Array.isArray(headers)) {
    return headers.some(
      (entry) =>
        Array.isArray(entry) &&
        String(entry[0]).toLowerCase().includes('user-agent') &&
        String(entry[1]).includes(GOOGLE_OTHER),
    );
  }

  return Object.entries(headers).some(
    ([name, value]) =>
      name.toLowerCase().includes('user-agent') &&
      String(value).includes(GOOGLE_OTHER),
  );
}

function hasGoogleOtherInContexts(
  contexts: Record<string, unknown> | undefined,
): boolean {
  const browser = contexts?.browser as
    { name?: unknown; user_agent?: unknown } | undefined;
  if (!browser) return false;

  return (
    String(browser.name ?? '').includes(GOOGLE_OTHER) ||
    String(browser.user_agent ?? '').includes(GOOGLE_OTHER)
  );
}

function hasGoogleOther(candidate: GtmDiagnosticsEventLike): boolean {
  return (
    hasGoogleOtherInHeaders(candidate.request) ||
    hasGoogleOtherInContexts(candidate.contexts)
  );
}

function hasGtmLatencyQuery(queryString: unknown): boolean {
  if (typeof queryString === 'string') {
    return queryString
      .split('&')
      .some(
        (entry) =>
          entry.split('=')[0] === GTM_LATENCY_KEY &&
          entry.split('=')[1] === '1',
      );
  }

  if (Array.isArray(queryString)) {
    return queryString.some((entry) => {
      if (Array.isArray(entry)) {
        return String(entry[0]) === GTM_LATENCY_KEY && String(entry[1]) === '1';
      }
      if (entry && typeof entry === 'object') {
        const record = entry as Record<string, unknown>;
        return (
          String(record.key) === GTM_LATENCY_KEY && String(record.value) === '1'
        );
      }
      return false;
    });
  }

  if (queryString && typeof queryString === 'object') {
    return Object.entries(queryString as Record<string, unknown>).some(
      ([key, value]) => key === GTM_LATENCY_KEY && String(value) === '1',
    );
  }

  return false;
}

function breadcrumbContainsGtmTd(breadcrumb: BreadcrumbLike): boolean {
  const candidates = [
    breadcrumb.message ?? '',
    breadcrumb.url ?? '',
    String(breadcrumb.data?.url ?? ''),
    String(breadcrumb.data?.urlQuery ?? ''),
  ];
  return candidates.some((value) => value.includes(GTM_TD_PATH_SNIPPET));
}

function hasGtmDiagnosticEvidence(candidate: GtmDiagnosticsEventLike): boolean {
  if (hasGtmLatencyQuery(candidate.request?.query_string)) return true;

  return candidate.breadcrumbs?.some(breadcrumbContainsGtmTd) ?? false;
}

export function isGoogleOtherGtmDiagnostics(event: unknown): boolean {
  if (event === null || typeof event !== 'object') {
    return false;
  }

  const candidate = event as GtmDiagnosticsEventLike;

  if (!hasExactGtmTypeErrorValue(candidate)) return false;
  if (!hasGoogleOther(candidate)) return false;
  if (!hasGtmDiagnosticEvidence(candidate)) return false;

  const frames = (candidate.exception?.values ?? []).flatMap(
    (exception) => exception.stacktrace?.frames ?? [],
  );

  if (frames.some(isLipsBusinessFrame)) return false;

  return true;
}
